// src/views/Demo.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";

// Extrait l'ID vidéo YouTube depuis n'importe quel format d'URL
// (watch?v=..., youtu.be/..., déjà en embed/...)
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

export default function Demo() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    parametres: null,
    temoignages: [],
    fonctionnalites: [],
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState("features");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data.parametres,
          temoignages: data.temoignages || [],
          fonctionnalites: data.fonctionnalites || [],
        });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handlePlayVideo = () => {
    setIsPlaying(true);
  };

  const params = pageData.parametres || {};
  const primaryColor = params.couleur_primaire || "#004080";
  const secondaryColor = params.couleur_secondaire || "#0066CC";
  const embedUrl = getYoutubeEmbedUrl(params.video_demo_url);

  // Témoignages actifs
  const activeTemoignages = pageData.temoignages.filter(t => t.actif);

  // Fonctionnalités pour le tab
  const featuresList = pageData.fonctionnalites.filter(f => f.actif !== false).slice(0, 6);

  const demoFeatures = [
    {
      id: "features",
      title: "Fonctionnalités clés",
      content: featuresList.length > 0
        ? featuresList.map(f => f.titre)
        : [
            "Suivi en temps réel des présences",
            "Notifications instantanées",
            "Messagerie sécurisée entre parents et enseignants",
            "Tableau de bord personnalisé",
            "Historique complet des absences",
            "Export des données en PDF/Excel"
          ]
    },
    {
      id: "benefits",
      title: "Avantages",
      content: [
        "Gain de temps pour les enseignants",
        "Communication facilitée avec les parents",
        "Suivi pédagogique amélioré",
        "Réduction des oublis et absences",
        "Interface intuitive et moderne",
        "Support 24/7"
      ]
    },
    {
      id: "testimonials",
      title: "Témoignages",
      content: activeTemoignages.length > 0
        ? activeTemoignages.map(t => `« ${t.texte} » — ${t.auteur}${t.role ? `, ${t.role}` : ''}${t.etablissement ? ` (${t.etablissement})` : ''}`)
        : [
            "« Enumera a transformé la communication dans notre école. » — Marie, Directrice",
            "« Les parents adorent recevoir les notifications en temps réel. » — Jean, Enseignant",
            "« Un outil indispensable pour notre établissement. » — Sophie, Secrétaire"
          ]
    }
  ];

  if (loading) {
    return (
      <div className="demo-page">
        <PublicTopBar />
        <div className="loader-container" style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div className="loader-spinner" style={{ width: 48, height: 48, border: "4px solid #e2e8f0", borderTopColor: primaryColor, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <PublicTopBar />

      {/* Hero Section */}
      <section className="demo-hero">
        <div className="demo-hero-bg" />
        <div className="demo-hero-container">
          <div className="demo-hero-content">
            <span className="demo-hero-badge">
              <i className="ti ti-video" />
              Demo en direct
            </span>
            <h1 className="demo-hero-title">
              Découvrez {params.nom_plateforme || "Enumera"} <br />
              <span className="demo-hero-accent" style={{ color: primaryColor }}>en action</span>
            </h1>
            <p className="demo-hero-desc">
              Regardez notre démo complète pour voir comment {params.nom_plateforme || "Enumera"} peut
              révolutionner la gestion de votre établissement scolaire.
            </p>
            <div className="demo-hero-actions">
              <button onClick={handlePlayVideo} className="btn btn-primary btn-lg" style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-player-play" />
                Voir la démo
              </button>
              <Link to="/contact" className="btn btn-ghost btn-lg">
                <i className="ti ti-message-circle" />
                Demander un essai
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="demo-video-section">
        <div className="demo-video-container">
          <div className="demo-video-wrapper">
            <div className="demo-video-player">
              {isPlaying && embedUrl ? (
                <iframe
                  className="demo-video"
                  src={`${embedUrl}?autoplay=1`}
                  title="Démo vidéo"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              ) : (
                <img
                  src={params.video_demo_poster || "https://via.placeholder.com/1280x720/004080/FFFFFF?text=Enumera+-+Demo+Video"}
                  alt="Aperçu démo"
                  className="demo-video"
                />
              )}

              {!isPlaying && (
                <div className="demo-video-overlay" onClick={handlePlayVideo}>
                  <button className="demo-play-button">
                    <i className="ti ti-player-play" />
                  </button>
                  <p className="demo-video-hint">Cliquez pour lancer la démo</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="demo-tabs-section">
        <div className="demo-tabs-container">
          <div className="demo-tabs-header">
            <h2 className="demo-tabs-title">
              Ce que vous allez découvrir
            </h2>
            <p className="demo-tabs-desc">
              Explorez les fonctionnalités et avantages d'{params.nom_plateforme || "Enumera"}
            </p>
          </div>

          <div className="demo-tabs">
            <div className="demo-tabs-nav">
              {demoFeatures.map((tab) => (
                <button
                  key={tab.id}
                  className={`demo-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={activeTab === tab.id ? {
                    color: primaryColor,
                    borderBottomColor: primaryColor
                  } : {}}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            <div className="demo-tabs-content">
              {demoFeatures.map((tab) => (
                <div
                  key={tab.id}
                  className={`demo-tab-panel ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <ul className="demo-tab-list">
                    {tab.content.map((item, index) => (
                      <li key={index}>
                        <i className="ti ti-check" style={{ color: primaryColor }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="demo-cta-section">
        <div className="demo-cta-container" style={{
          background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}06)`
        }}>
          <div className="demo-cta-content">
            <h2 className="demo-cta-title">
              Convaincu par la démo ?
            </h2>
            <p className="demo-cta-desc">
              Rejoignez les établissements qui utilisent déjà {params.nom_plateforme || "Enumera"}
            </p>
            <div className="demo-cta-actions">
              <Link to="/contact" className="btn btn-primary btn-lg" style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-rocket" />
                Commencer maintenant
              </Link>
              <Link to="/features" className="btn btn-ghost btn-lg">
                <i className="ti ti-list" />
                Voir toutes les fonctionnalités
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        .demo-page {
          min-height: 100vh;
          background: #ffffff;
          color: #0f172a;
        }

        /* === HERO SECTION === */
        .demo-hero {
          position: relative;
          padding: 120px 32px 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #e6eef7 100%);
          overflow: hidden;
        }

        .demo-hero-bg {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 30% 50%, rgba(0, 64, 128, 0.05) 0%, transparent 60%),
            radial-gradient(ellipse at 70% 50%, rgba(0, 102, 204, 0.03) 0%, transparent 60%);
          pointer-events: none;
        }

        .demo-hero-container {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
        }

        .demo-hero-content {
          text-align: center;
        }

        .demo-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: rgba(0, 64, 128, 0.08);
          border: 1px solid rgba(0, 64, 128, 0.15);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: #004080;
          margin-bottom: 16px;
        }

        .demo-hero-title {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 16px;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .demo-hero-accent {
          color: #004080;
        }

        .demo-hero-desc {
          font-size: 18px;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto 32px;
          line-height: 1.6;
        }

        .demo-hero-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, #004080, #0066CC);
          color: #ffffff;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 64, 128, 0.3);
        }

        .btn-ghost {
          background: rgba(15, 23, 42, 0.04);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-ghost:hover {
          background: rgba(15, 23, 42, 0.08);
        }

        .btn-lg {
          padding: 14px 32px;
          font-size: 16px;
        }

        /* === VIDEO SECTION === */
        .demo-video-section {
          padding: 60px 32px 80px;
          background: #f8fafc;
        }

        .demo-video-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .demo-video-wrapper {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          padding: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }

        .demo-video-player {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          background: #0f172a;
          aspect-ratio: 16/9;
        }

        .demo-video {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .demo-video-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.5);
          cursor: pointer;
          transition: background 0.3s ease;
        }

        .demo-video-overlay:hover {
          background: rgba(15, 23, 42, 0.3);
        }

        .demo-play-button {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(0, 64, 128, 0.9);
          border: 3px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          font-size: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .demo-play-button:hover {
          transform: scale(1.1);
          background: #004080;
          box-shadow: 0 0 40px rgba(0, 64, 128, 0.3);
        }

        .demo-video-hint {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          margin-top: 16px;
          letter-spacing: 0.03em;
        }

        /* === TABS SECTION === */
        .demo-tabs-section {
          padding: 80px 32px;
          background: #ffffff;
        }

        .demo-tabs-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .demo-tabs-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .demo-tabs-title {
          font-size: 36px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #0f172a;
        }

        .demo-tabs-desc {
          font-size: 16px;
          color: #64748b;
        }

        .demo-tabs {
          background: #f8fafc;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .demo-tabs-nav {
          display: flex;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .demo-tab-btn {
          flex: 1;
          padding: 16px 24px;
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 600;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .demo-tab-btn:hover {
          color: #0f172a;
          background: rgba(0, 64, 128, 0.02);
        }

        .demo-tab-btn.active {
          color: #004080;
          background: #f8fafc;
        }

        .demo-tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 3px;
          background: #004080;
          border-radius: 2px;
        }

        .demo-tabs-content {
          padding: 32px;
        }

        .demo-tab-panel {
          display: none;
        }

        .demo-tab-panel.active {
          display: block;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .demo-tab-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px 24px;
        }

        .demo-tab-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          font-size: 15px;
          color: #0f172a;
        }

        .demo-tab-list li i {
          color: #10b981;
          font-size: 18px;
          flex-shrink: 0;
        }

        /* === CTA SECTION === */
        .demo-cta-section {
          padding: 80px 32px;
          background: #f8fafc;
        }

        .demo-cta-container {
          max-width: 1280px;
          margin: 0 auto;
          background: linear-gradient(135deg, #e6eef7, #f0f4ff);
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          padding: 56px 40px;
          text-align: center;
        }

        .demo-cta-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 12px;
          color: #0f172a;
        }

        .demo-cta-desc {
          font-size: 16px;
          color: #64748b;
          margin: 0 0 24px;
        }

        .demo-cta-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* === RESPONSIVE === */
        @media (max-width: 1024px) {
          .demo-hero-title {
            font-size: 36px;
          }
        }

        @media (max-width: 768px) {
          .demo-hero {
            padding: 100px 20px 60px;
          }

          .demo-hero-title {
            font-size: 30px;
          }

          .demo-video-section {
            padding: 40px 20px 60px;
          }

          .demo-tabs-section {
            padding: 60px 20px;
          }

          .demo-tabs-nav {
            flex-direction: column;
          }

          .demo-tab-btn {
            padding: 12px 20px;
            text-align: center;
          }

          .demo-tab-btn.active::after {
            display: none;
          }

          .demo-tab-btn.active {
            background: rgba(0, 64, 128, 0.06);
          }

          .demo-tab-list {
            grid-template-columns: 1fr;
          }

          .demo-cta-section {
            padding: 60px 20px;
          }

          .demo-cta-container {
            padding: 36px 24px;
          }

          .demo-cta-title {
            font-size: 26px;
          }

          .demo-play-button {
            width: 60px;
            height: 60px;
            font-size: 28px;
          }
        }

        @media (max-width: 480px) {
          .demo-hero-title {
            font-size: 24px;
          }

          .demo-hero-desc {
            font-size: 15px;
          }

          .demo-tabs-content {
            padding: 20px;
          }

          .demo-tab-list li {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
}